import React, { useMemo } from "react";
import { Box, Code, Heading, Stack, Text } from "@chakra-ui/react";
import { Issue } from "./types";
import { IssueCard } from "./Issues";

// Entity-centric browse. Iterates issuesByEntity → for each entity pointer,
// render a panel of every issue whose primary location lives under that
// entity. Entities are listed in the order their pointers first appear in
// `issuesByEntity` (which the server emits in document order).
export const EntityView: React.FC<{
  issues: Issue[];
  issuesByEntity: Record<string, string[]>;
}> = ({ issues, issuesByEntity }) => {
  const issueById = useMemo(() => {
    const map = new Map<string, Issue>();
    for (const i of issues) map.set(i.id, i);
    return map;
  }, [issues]);

  const entities = Object.entries(issuesByEntity);
  if (entities.length === 0) {
    return <Text color="fg.muted">No entity-level issues to display.</Text>;
  }

  return (
    <Stack gap="20px">
      {entities.map(([pointer, issueIds]) => {
        const entityIssues = issueIds
          .map((id) => issueById.get(id))
          .filter((i): i is Issue => Boolean(i));
        return (
          <Box
            key={pointer || "document-root"}
            borderWidth="1px"
            borderRadius="md"
            padding="14px 16px"
            bg="bg.panel"
          >
            <Heading size="sm" marginBottom="10px">
              Entity at <Code size="sm">{pointer || "/"}</Code>{" "}
              <Text as="span" fontSize="xs" color="fg.muted">
                ({entityIssues.length} issue
                {entityIssues.length === 1 ? "" : "s"})
              </Text>
            </Heading>
            <Stack gap="10px">
              {entityIssues.map((issue) => (
                <IssueCard key={issue.id} issue={issue} />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};
